import styled from "styled-components";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Modal from "../../../components/Modal/Modal";
import Tooltip from "../../../components/Tooltip/Tooltip";
import AIHumansPreviewPlayer from "./AIHumansPreviewPlayer";
import Button, { ButtonThemes } from "../../../components/Button/Button";
import IconButton, { IconButtonThemes } from "../../../components/Button/IconButton";
import Textfield, { TextfieldVariant } from "../../../components/Textfield/Textfield";
import {
  CheckSquareIcon,
  CheckIconExtraThick,
  CircularProgress,
  EditIconSimple,
  MenuBurgerIcon,
} from "../../../components/Icons/Icons";
import { CustomInputFieldStyles } from "./tabs/tabs.styles";
import { Status } from "../../../redux/actions/humansProjectActions";
import { HumansParagraph, Project } from "../../../types/humansProject";

type SaveButtonProps = {
  humansProject: Project | null;
  humansProjectLoading: boolean;
  createVideo: () => void;
  updateVideo: () => void;
};
interface SubmitButtonProps {
  status?: Status;
  isGuest?: boolean;
  disabled: boolean;
  isNotEnoughCredits: boolean;
  signUpPopupOpen?: () => void;
  generativeVideoPopupOpen: () => void;
  notEnoughCreditsPopupOpen: () => void;
}
interface TopNavbarProps {
  paragraphs: HumansParagraph[];
  projectTitle: string;
  handleProjectTitleChange: (value: string) => void;
  saveButtonProps: SaveButtonProps;
  submitButtonProps: SubmitButtonProps;
}

export const TopNavbarMenuButton = ({ action, disabled = false }: { action?: () => void; disabled?: boolean }) => {
  const navigate = useNavigate();

  return (
    <MenuButtonWrapper>
      <IconButton
        iconButtonTheme={IconButtonThemes.Transparent}
        icon={<MenuBurgerIcon />}
        // onClick={handleSubSidebar} // to open and close sidebar
        onClick={() => (action ? action() : navigate("/"))}
        disabled={disabled}
      />
      <Line />
    </MenuButtonWrapper>
  );
};

const TopNavbar = ({
  paragraphs,
  projectTitle,
  handleProjectTitleChange,
  saveButtonProps,
  submitButtonProps,
}: TopNavbarProps) => {
  const { createVideo, updateVideo, humansProject, humansProjectLoading } = saveButtonProps;
  const {
    disabled,
    status,
    isGuest,
    isNotEnoughCredits,
    notEnoughCreditsPopupOpen,
    generativeVideoPopupOpen,
    signUpPopupOpen,
  } = submitButtonProps;
  const [openEditTitlePopup, setOpenEditTitlePopup] = useState(false);
  const [title, setTitle] = useState<string>("");

  const handleOpenEditTitlePopupClose = () => {
    if (humansProject?.projectId) {
      setOpenEditTitlePopup(true);
    } else {
      toast.warn("Save the project first to edit the title");
    }
  };

  const handleCloseEditTitlePopup = () => {
    setTitle("");
    setOpenEditTitlePopup(false);
  };
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value);
  const handleSaveTitle = () => {
    if (title && title.trim()) {
      handleProjectTitleChange(title);
      handleCloseEditTitlePopup();
    }
  };

  useEffect(() => {
    setTitle(projectTitle);
  }, [projectTitle]);

  return (
    <Wrapper>
      <TopNavbarMenuButton />

      <CenteredWrapper>
        <p>{projectTitle}</p>
        <div onClick={handleOpenEditTitlePopupClose}>
          <EditIconSimple />
        </div>
      </CenteredWrapper>

      <FlexWrapper>
        <Button
          buttonTheme={ButtonThemes.Transparent}
          disabled={humansProjectLoading}
          text="Save as draft"
          icon={humansProjectLoading ? <CircularProgress color="#3100f7" /> : <CheckSquareIcon color="#ffffff" />}
          onClick={humansProject?.projectId ? updateVideo : createVideo}
        />
        <AIHumansPreviewPlayer paragraphs={paragraphs} />
        <SubmitButtonWrapper>
          <SubmitButton
            // text={status === Status.isProgress ? "Generating Video..." : "Create Video"}
            text="Submit"
            disabled={!disabled}
            onClick={
              !isGuest ? (isNotEnoughCredits ? notEnoughCreditsPopupOpen : generativeVideoPopupOpen) : signUpPopupOpen
            }
            icon={<CheckIconExtraThick />}
          />
          <Tooltip
            text={
              status === Status.isProgress
                ? "The project is being generated"
                : "Save the project before creating a video"
            }
            position="left"
            style={{ boxShadow: "none", borderRadius: "4px", padding: "4px 8px 6px" }}
            reverseColor
            arrow
          />
        </SubmitButtonWrapper>
      </FlexWrapper>
      <Modal
        className="update-video-title"
        maxWidth={523}
        maxHeight={264}
        open={openEditTitlePopup}
        onClose={handleCloseEditTitlePopup}
        closeIcon={false}
      >
        <ModelContentWrapper>
          <CustomInputFieldStyles inputStyles={{ height: 44, borderRadius: 4 }}>
            <Textfield
              placeholder="Untitled session"
              label="Title"
              value={title}
              onChange={handleTitleChange}
              required
              variant={TextfieldVariant.noneAdornment}
            />
          </CustomInputFieldStyles>
          <FlexWrapper>
            <Button buttonTheme={ButtonThemes.White} text="Cancel" onClick={handleCloseEditTitlePopup} />
            <Button
              buttonTheme={ButtonThemes.Primary}
              text="Save"
              onClick={handleSaveTitle}
              disabled={!title && !title.trim()}
            />
          </FlexWrapper>
        </ModelContentWrapper>
      </Modal>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  /* background-color: ${({ theme }) => theme.secondaryBackground1}; */
  background-color: #0e0d0d;
  color: #ffffff;
  width: 100%;
  height: 51px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  position: relative;
`;
const MenuButtonWrapper = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;
const FlexWrapper = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;

  & button {
    gap: 4px;
    svg > path {
      fill: unset;
    }
    span {
      white-space: nowrap;
    }
    .loader {
      svg > path {
        fill: #ffffff;
      }
    }
  }
`;
const CenteredWrapper = styled(FlexWrapper)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  gap: 12px;
  p {
    padding: 4px;
  }
`;

const Line = styled.div`
  height: 30px;
  width: 1px;
  background-color: white;
`;
const SubmitButtonWrapper = styled.div`
  position: relative;
  & > div {
    width: max-content;
  }
  &:hover > div {
    opacity: 1;
    visibility: visible;
  }
`;

const SubmitButton = styled(Button)`
  height: 34px;
  width: 95px;
  border-radius: 5px;
  background: #3100f7;
  gap: 4px;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`;
const ModelContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;
export default TopNavbar;
